// src/pos-app/PosAppShell.js
//
// Native app boot shell (F12a Task 4.1). This is the front door for the
// Capacitor POS app — mounted instead of the web landing/route tree when
// `host.type === 'capacitor'` (see App.js). Responsibilities for this task:
//   1. Load the persisted station config via StationContext.
//   2. Hide the native splash screen once we know what to render.
//   3. Render the EnrollmentScreen when no station is configured yet, then
//      the real station shell for the configured station (cashier — Phase 7;
//      waiter — Phase 8; KDS — Phase 9).
import React, { useEffect, useState } from 'react';
import host from '../host';
import { StationProvider, useStation } from '../station/StationContext';
import { STATIONS } from '../station/stationConfig';
import { ensureBackendBinding } from '../platform/backendBinding';
import { ConnectionProvider } from './ConnectionStatus';
import EnrollmentScreen from './EnrollmentScreen';
import CashierStation from './stations/CashierStation';
import WaiterStation from './stations/WaiterStation';
import WaiterSalesStation from './stations/WaiterSalesStation';
import KdsStation from './stations/KdsStation';
import { C, FONT } from '../styles/theme';

const screenStyle = {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: C.bg,
    fontFamily: FONT,
    padding: 24,
    boxSizing: 'border-box',
};

const subtextStyle = {
    fontSize: 13,
    color: C.muted,
    margin: 0,
};

function LoadingView() {
    return (
        <div style={screenStyle}>
            <p style={subtextStyle}>Loading…</p>
        </div>
    );
}

function PosAppShellInner() {
    const { config, loading } = useStation();

    useEffect(() => {
        if (loading) return;
        if (host.type !== 'capacitor') return;

        (async () => {
            try {
                const { SplashScreen } = await import('@capacitor/splash-screen');
                await SplashScreen.hide();
            } catch (err) {
                // Never let a missing/failed native plugin block the app.
            }
        })();
    }, [loading]);

    if (loading) {
        return <LoadingView />;
    }

    if (config == null) {
        return <EnrollmentScreen />;
    }

    if (config.station === STATIONS.CASHIER) {
        return <CashierStation />;
    }

    // TABLE_SERVICE and WAITER are the SAME stored value ('waiter') — see
    // station/stationConfig.js. Devices enrolled before the F15 part 2 rename
    // land here unchanged; only the label they were enrolled under differs.
    if (config.station === STATIONS.TABLE_SERVICE) {
        return <WaiterStation />;
    }

    if (config.station === STATIONS.WAITER_SALES) {
        return <WaiterSalesStation />;
    }

    if (config.station === STATIONS.KDS) {
        return <KdsStation />;
    }

    // Unrecognized persisted station (corrupt/legacy config) — recover by
    // re-enrolling the device rather than rendering a dead screen.
    return <EnrollmentScreen />;
}

export default function PosAppShell() {
    // Backend-binding gate (F12a-N Issue A).
    //
    // SECONDARY LAYER as of task-n8 Fix B: the authoritative call now
    // happens in src/index.js, awaited BEFORE `root.render()` — before
    // AuthProvider (an ANCESTOR of this component) ever mounts, closing a
    // race this component-level gate could not: AuthContext's own boot
    // effect starts restoring a persisted session as soon as AuthProvider
    // mounts, which is not ordered against a descendant's effect in a way
    // that guarantees this check runs first (see index.js's header comment
    // for the full explanation). This effect is kept as a harmless,
    // idempotent second check — ensureBackendBinding() no-ops when the
    // marker already matches — for any boot path that somehow reaches this
    // component without having gone through index.js's render call (hot
    // reload in dev, tests mounting PosAppShell directly, etc.).
    const [bindingReady, setBindingReady] = useState(host.type !== 'capacitor');

    useEffect(() => {
        if (host.type !== 'capacitor') return;

        let cancelled = false;
        (async () => {
            try {
                await ensureBackendBinding();
            } catch (err) {
                // Never let a binding-check failure block boot.
            } finally {
                if (!cancelled) setBindingReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    if (!bindingReady) {
        return <LoadingView />;
    }

    return (
        <ConnectionProvider>
            <StationProvider>
                <PosAppShellInner />
            </StationProvider>
        </ConnectionProvider>
    );
}
